// /admin/reports — T2 trust + safety moderation queue.
//
// Consumes the admin API at /api/admin/reports (list) and
// /api/admin/reports/:id/resolve (mutation). User-facing report submission
// lives elsewhere (POST /api/reports + the ReportButton component); this is
// the triage surface.
//
// Layout: PageHero + filter chips + paginated table + click-to-open detail
// drawer. The selected report id and active status filter are mirrored to
// the URL (`?status=...&selected=...`) so reloads + deep links preserve state.
// Detail drawer shows the full report metadata + body + a resolution form
// (when the report is still open or reviewing). Resolution success invalidates
// the list query + closes the drawer + toasts.

import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Drawer,
  Group,
  Loader,
  Pagination,
  Radio,
  Stack,
  Table,
  Text,
  Textarea,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { AdminShell } from '../../components/admin-shell/AdminShell';
import { StateBadge } from '../../components/StateBadge';
import { EmptyState, PageHero } from '../../components/ui';
import { ApiError, fetchJson, mutateJson } from '../../lib/api';
import { showToast } from '../../lib/toast';

// ---------------------------------------------------------------------------
// Wire types — mirror the worker route's response shape exactly.
// ---------------------------------------------------------------------------

type ReportSubjectType = 'venue' | 'artist' | 'message' | 'review' | 'exchange';
type ReportCategory =
  | 'safety'
  | 'harassment'
  | 'spam_or_fake'
  | 'misrepresentation'
  | 'inappropriate_content'
  | 'other';
type ReportStatus = 'open' | 'reviewing' | 'actioned' | 'dismissed';

interface ReportItem {
  id: string;
  subject_type: ReportSubjectType;
  subject_id: string;
  subject_label: string | null;
  subject_owner_user_id: string | null;
  subject_owner_display_name: string | null;
  category: ReportCategory;
  serious: boolean;
  body: string;
  status: ReportStatus;
  reporter_user_id: string;
  reporter_display_name: string | null;
  resolved_by_user_id: string | null;
  resolved_at: number | null;
  resolution_note: string | null;
  created_at: number;
}

interface ListResponse {
  items: ReportItem[];
  total: number;
}

interface ResolveResponse {
  ok: true;
  /** Set when the resolve also suspended the reported party pending review. */
  suspended_user_id: string | null;
  report: ReportItem;
}

// ---------------------------------------------------------------------------
// Filter chip + URL search-param schema.
// ---------------------------------------------------------------------------

const STATUS_FILTERS = ['open', 'reviewing', 'actioned', 'dismissed', 'all'] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

const STATUS_FILTER_LABELS: Record<StatusFilter, string> = {
  open: 'Open',
  reviewing: 'Reviewing',
  actioned: 'Actioned',
  dismissed: 'Dismissed',
  all: 'All',
};

type ReportsSearch = {
  status: StatusFilter;
  page: number;
  selected?: string;
};

const PAGE_SIZE = 25;

// ---------------------------------------------------------------------------
// Category label map. Duplicated from ReportButton.tsx on purpose — both
// surfaces should evolve together but the wire enum is shared, so duplication
// is the smaller evil than a cross-component dependency just for copy.
// ---------------------------------------------------------------------------

const CATEGORY_LABELS: Record<ReportCategory, string> = {
  safety: 'Someone could get hurt',
  harassment: 'Harassment or abuse',
  spam_or_fake: 'Spam or fake account',
  misrepresentation: "Photos / details don't match reality",
  inappropriate_content: 'Inappropriate content',
  other: 'Something else',
};

const SUBJECT_TYPE_LABELS: Record<ReportSubjectType, string> = {
  venue: 'Venue',
  artist: 'Artist',
  message: 'Message',
  review: 'Review',
  exchange: 'Exchange',
};

// ---------------------------------------------------------------------------
// Route definition.
// ---------------------------------------------------------------------------

export const Route = createFileRoute('/admin/reports')({
  validateSearch: (search: Record<string, unknown>): ReportsSearch => {
    const out: ReportsSearch = { status: 'open', page: 1 };
    if (
      typeof search.status === 'string' &&
      (STATUS_FILTERS as ReadonlyArray<string>).includes(search.status)
    ) {
      out.status = search.status as StatusFilter;
    }
    if (typeof search.page === 'number' && Number.isInteger(search.page) && search.page >= 1) {
      out.page = search.page;
    } else if (typeof search.page === 'string') {
      const parsed = Number.parseInt(search.page, 10);
      if (Number.isFinite(parsed) && parsed >= 1) out.page = parsed;
    }
    if (typeof search.selected === 'string' && search.selected.length > 0) {
      out.selected = search.selected;
    }
    return out;
  },
  component: AdminReportsPage,
});

// ---------------------------------------------------------------------------
// Page component.
// ---------------------------------------------------------------------------

function AdminReportsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const offset = (search.page - 1) * PAGE_SIZE;
  const listKey = useMemo(
    () => ['admin', 'reports', search.status, search.page] as const,
    [search.status, search.page],
  );

  const { data, isLoading, error } = useQuery({
    queryKey: listKey,
    queryFn: () =>
      fetchJson<ListResponse>(
        `/api/admin/reports?status=${encodeURIComponent(search.status)}&limit=${PAGE_SIZE}&offset=${offset}`,
      ),
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Resolve the selected report against the loaded page. If the URL points
  // at a report that isn't in this page (e.g. after a filter change), close
  // the drawer rather than render a stale shape.
  const selectedReport = useMemo(
    () => (search.selected ? items.find((r) => r.id === search.selected) ?? null : null),
    [items, search.selected],
  );

  const setStatus = (next: StatusFilter) => {
    void navigate({
      to: '/admin/reports',
      // Filter change resets pagination + clears any open drawer.
      search: { status: next, page: 1 },
      replace: true,
    });
  };

  const setPage = (next: number) => {
    void navigate({
      to: '/admin/reports',
      search: { status: search.status, page: next },
      replace: true,
    });
  };

  const openReport = (id: string) => {
    void navigate({
      to: '/admin/reports',
      search: { status: search.status, page: search.page, selected: id },
      replace: true,
    });
  };

  const closeDrawer = () => {
    void navigate({
      to: '/admin/reports',
      search: { status: search.status, page: search.page },
      replace: true,
    });
  };

  return (
    <AdminShell>
      <Stack gap="lg">
        <PageHero
          eyebrow="Trust & safety"
          title="Reports"
          lede="The moderation queue. Triage what members flag, action what needs action, and close the loop with the reporter."
          tinted={false}
        />

        <Group gap="xs" wrap="wrap">
          {STATUS_FILTERS.map((value) => (
            <Chip
              key={value}
              checked={search.status === value}
              onChange={() => setStatus(value)}
              variant="filled"
              size="sm"
            >
              {STATUS_FILTER_LABELS[value]}
            </Chip>
          ))}
        </Group>

        <ReportsList
          items={items}
          isLoading={isLoading}
          error={error}
          statusFilter={search.status}
          onOpen={openReport}
        />

        {total > 0 ? (
          <Group justify="space-between" align="center">
            <Text c="dimmed" size="sm">
              {paginationLabel(offset, items.length, total)}
            </Text>
            <Pagination value={search.page} onChange={setPage} total={totalPages} size="sm" />
          </Group>
        ) : null}
      </Stack>

      <Drawer
        opened={selectedReport !== null}
        onClose={closeDrawer}
        position="right"
        size="lg"
        title="Report detail"
        padding="lg"
      >
        {selectedReport ? (
          <ReportDetail
            report={selectedReport}
            onResolved={() => {
              void qc.invalidateQueries({ queryKey: ['admin', 'reports'] });
              closeDrawer();
            }}
            onConflict={() => {
              showToast({
                kind: 'warning',
                title: 'Report changed',
                body: 'This report changed while you were resolving it. Reloading...',
              });
              void qc.invalidateQueries({ queryKey: ['admin', 'reports'] });
              closeDrawer();
            }}
          />
        ) : null}
      </Drawer>
    </AdminShell>
  );
}

// ---------------------------------------------------------------------------
// List section — table on lg+, stacked cards below lg.
// ---------------------------------------------------------------------------

function ReportsList({
  items,
  isLoading,
  error,
  statusFilter,
  onOpen,
}: {
  items: ReportItem[];
  isLoading: boolean;
  error: unknown;
  statusFilter: StatusFilter;
  onOpen: (id: string) => void;
}) {
  if (isLoading) {
    return (
      <Group>
        <Loader size="sm" />
        <Text c="dimmed">Loading reports...</Text>
      </Group>
    );
  }

  if (error) {
    return (
      <EmptyState
        title="Could not load reports"
        body={error instanceof Error ? error.message : 'Something went wrong fetching the queue.'}
      />
    );
  }

  if (items.length === 0) {
    return <EmptyState title={emptyTitle(statusFilter)} body={emptyBody(statusFilter)} />;
  }

  return (
    <>
      {/* Desktop table */}
      <Box visibleFrom="lg">
        <Table striped highlightOnHover verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Subject</Table.Th>
              <Table.Th>Category</Table.Th>
              <Table.Th>Reporter</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Created</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((r) => (
              <Table.Tr
                key={r.id}
                style={{ cursor: 'pointer' }}
                onClick={() => onOpen(r.id)}
                // FR-140: keyboard-only admins get no other way to open a report
                // from this queue, so make the row itself operable.
                tabIndex={0}
                role="button"
                aria-label={`Open report: ${SUBJECT_TYPE_LABELS[r.subject_type]} ${r.subject_label ?? r.subject_id.slice(0, 8)}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpen(r.id);
                  }
                }}
              >
                <Table.Td>
                  <Stack gap={2}>
                    <Text size="sm" fw={500}>
                      {SUBJECT_TYPE_LABELS[r.subject_type]} ·{' '}
                      {r.subject_label ?? r.subject_id.slice(0, 8)}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {r.subject_id.slice(0, 8)}
                    </Text>
                  </Stack>
                </Table.Td>
                <Table.Td>
                  <Group gap="xs" wrap="nowrap">
                    {r.serious ? (
                      <Badge color="red" variant="filled" size="sm">
                        Serious
                      </Badge>
                    ) : null}
                    <Text size="sm">{CATEGORY_LABELS[r.category]}</Text>
                  </Group>
                </Table.Td>
                <Table.Td>{r.reporter_display_name ?? r.reporter_user_id.slice(0, 8)}</Table.Td>
                <Table.Td>
                  <StateBadge entity="report" state={r.status} />
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {formatRelative(r.created_at)}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Box>

      {/* Mobile card list */}
      <Stack hiddenFrom="lg" gap="sm">
        {items.map((r) => (
          <Card
            key={r.id}
            withBorder
            padding="md"
            radius="md"
            onClick={() => onOpen(r.id)}
            style={{ cursor: 'pointer' }}
            // FR-140: same keyboard access on the mobile card variant.
            tabIndex={0}
            role="button"
            aria-label={`Open report: ${SUBJECT_TYPE_LABELS[r.subject_type]} ${r.subject_label ?? r.subject_id.slice(0, 8)}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen(r.id);
              }
            }}
          >
            <Stack gap="xs">
              <Group justify="space-between" wrap="nowrap">
                <Text size="sm" fw={600} style={{ minWidth: 0 }} lineClamp={1}>
                  {SUBJECT_TYPE_LABELS[r.subject_type]} ·{' '}
                  {r.subject_label ?? r.subject_id.slice(0, 8)}
                </Text>
                <StateBadge entity="report" state={r.status} size="sm" />
              </Group>
              <Group gap="xs" wrap="nowrap">
                {r.serious ? (
                  <Badge color="red" variant="filled" size="sm">
                    Serious
                  </Badge>
                ) : null}
                <Text size="sm" c="ink.7">
                  {CATEGORY_LABELS[r.category]}
                </Text>
              </Group>
              <Group gap="xs" justify="space-between">
                <Text size="xs" c="dimmed">
                  by {r.reporter_display_name ?? r.reporter_user_id.slice(0, 8)}
                </Text>
                <Text size="xs" c="dimmed">
                  {formatRelative(r.created_at)}
                </Text>
              </Group>
            </Stack>
          </Card>
        ))}
      </Stack>
    </>
  );
}

// ---------------------------------------------------------------------------
// Detail drawer body.
// ---------------------------------------------------------------------------

function ReportDetail({
  report,
  onResolved,
  onConflict,
}: {
  report: ReportItem;
  onResolved: () => void;
  onConflict: () => void;
}) {
  const subjectHref = resolveSubjectHref(report);
  const isClosed = report.status === 'actioned' || report.status === 'dismissed';

  const [action, setAction] = useState<'actioned' | 'dismissed'>('actioned');
  const [note, setNote] = useState('');
  const [suspendSubject, setSuspendSubject] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');

  // The reported party is the person a suspend would land on. We can name them
  // for venue/artist (subject owner); for message/exchange the server resolves
  // the user at resolve time, so we describe rather than name.
  const suspendTargetLabel = report.subject_owner_display_name;

  const resolve = useMutation({
    mutationFn: () =>
      mutateJson<ResolveResponse>(`/api/admin/reports/${report.id}/resolve`, {
        status: action,
        ...(note.trim().length > 0 ? { resolution_note: note.trim() } : {}),
        ...(suspendSubject
          ? {
              suspend_subject: true,
              ...(suspendReason.trim().length > 0
                ? { suspend_reason: suspendReason.trim() }
                : {}),
            }
          : {}),
      }),
    onSuccess: (res) => {
      showToast({
        kind: 'success',
        title: res.suspended_user_id !== null ? 'Report closed, member suspended' : 'Report closed',
        body:
          res.suspended_user_id !== null
            ? 'The reporter has been notified and the reported member is suspended pending review.'
            : 'The reporter has been notified.',
      });
      onResolved();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        onConflict();
        return;
      }
      showToast({
        kind: 'error',
        title: 'Could not resolve report',
        body: err instanceof Error ? err.message : 'Try again in a moment.',
      });
    },
  });

  return (
    <Stack gap="lg">
      <Stack gap={4}>
        <Group gap="xs">
          <StateBadge entity="report" state={report.status} />
          <Text size="sm" c="dimmed">
            Reported {formatRelative(report.created_at)}
          </Text>
        </Group>
        <Text fw={600} size="lg">
          {SUBJECT_TYPE_LABELS[report.subject_type]} ·{' '}
          {report.subject_label ?? report.subject_id.slice(0, 8)}
        </Text>
      </Stack>

      <KVStack>
        <KV label="Category" value={CATEGORY_LABELS[report.category]} />
        <KV
          label="Reporter"
          value={report.reporter_display_name ?? report.reporter_user_id}
        />
        {report.subject_owner_display_name ? (
          <KV
            label="Subject owner"
            value={report.subject_owner_display_name}
          />
        ) : null}
        <KV
          label="Subject"
          value={
            subjectHref ? (
              <Anchor href={subjectHref} target="_blank" rel="noopener">
                {subjectHref}
              </Anchor>
            ) : (
              <Text size="sm" c="ink.7" style={{ fontFamily: 'monospace' }}>
                {report.subject_id}
              </Text>
            )
          }
        />
        {report.resolved_at !== null ? (
          <KV
            label="Resolved"
            value={`${formatAbsolute(report.resolved_at)} (${formatRelative(report.resolved_at)})`}
          />
        ) : null}
        {report.resolution_note ? (
          <KV label="Resolution note" value={report.resolution_note} />
        ) : null}
      </KVStack>

      <Card withBorder padding="md" radius="md">
        <Stack gap={6}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
            What happened
          </Text>
          <Text size="sm" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
            {report.body}
          </Text>
        </Stack>
      </Card>

      {!isClosed ? (
        <Card withBorder padding="md" radius="md">
          <Stack gap="md">
            <Text fw={600}>Resolve this report</Text>
            <Radio.Group
              value={action}
              onChange={(v) => setAction(v as 'actioned' | 'dismissed')}
              label="Decision"
            >
              <Stack gap="xs" mt="xs">
                <Radio value="actioned" label="Action this. Take a follow-up step" />
                <Radio value="dismissed" label="Dismiss. No further action needed" />
              </Stack>
            </Radio.Group>
            <Textarea
              label="Resolution note (optional)"
              description="Shown to the reporter so they know how the team responded. Up to 2000 characters."
              value={note}
              onChange={(e) => setNote(e.currentTarget.value)}
              minRows={3}
              maxLength={2000}
            />

            {/* One-click safety action. Emphasised for serious flags — the
                suspend lands on the reported party pending review. */}
            <Alert
              color={report.serious ? 'red' : 'gray'}
              variant={report.serious ? 'light' : 'outline'}
              title={report.serious ? 'Serious report' : 'Safety action'}
            >
              <Stack gap="sm">
                {report.serious ? (
                  <Text size="sm">
                    A serious safety or harassment flag. If this is credible, suspend the
                    reported member now and review afterwards.
                  </Text>
                ) : null}
                <Checkbox
                  checked={suspendSubject}
                  onChange={(e) => setSuspendSubject(e.currentTarget.checked)}
                  label={
                    suspendTargetLabel
                      ? `Suspend ${suspendTargetLabel} pending review`
                      : 'Suspend this member pending review'
                  }
                  description="Ends their sessions and blocks sign-in until the team clears it. Reversible from the member's admin page."
                />
                {suspendSubject ? (
                  <Textarea
                    label="Suspension reason"
                    description="Recorded on the audit log and shared with the suspended member."
                    placeholder="Suspended pending review following a safety report."
                    value={suspendReason}
                    onChange={(e) => setSuspendReason(e.currentTarget.value)}
                    minRows={2}
                    maxLength={2000}
                  />
                ) : null}
              </Stack>
            </Alert>

            <Group justify="flex-end">
              <Button
                {...(suspendSubject ? { color: 'red' } : {})}
                onClick={() => resolve.mutate()}
                loading={resolve.isPending}
                disabled={resolve.isPending}
              >
                {suspendSubject ? 'Close report & suspend' : 'Close report'}
              </Button>
            </Group>
          </Stack>
        </Card>
      ) : (
        <Text size="sm" c="dimmed">
          This report is closed. No further action available here.
        </Text>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Small helpers.
// ---------------------------------------------------------------------------

function KVStack({ children }: { children: React.ReactNode }) {
  return <Stack gap={6}>{children}</Stack>;
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Group gap="sm" align="flex-start" wrap="nowrap">
      <Text c="dimmed" size="sm" style={{ minWidth: 120, flexShrink: 0 }}>
        {label}
      </Text>
      <Box style={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
        {typeof value === 'string' ? <Text size="sm">{value}</Text> : value}
      </Box>
    </Group>
  );
}

function paginationLabel(offset: number, pageCount: number, total: number): string {
  if (total === 0) return '0 total';
  const start = offset + 1;
  const end = offset + pageCount;
  return `Showing ${start}-${end} of ${total}`;
}

function emptyTitle(filter: StatusFilter): string {
  switch (filter) {
    case 'open':
      return 'No open reports';
    case 'reviewing':
      return 'Nothing under review';
    case 'actioned':
      return 'No actioned reports yet';
    case 'dismissed':
      return 'No dismissed reports yet';
    case 'all':
      return 'No reports yet';
  }
}

function emptyBody(filter: StatusFilter): string {
  switch (filter) {
    case 'open':
      return 'The queue is clear. New reports will land here as members flag content.';
    case 'reviewing':
      return 'Reports being actively triaged will surface here once that workflow exists.';
    case 'actioned':
      return 'Closed reports with a follow-up step will appear here.';
    case 'dismissed':
      return 'Reports closed without further action will appear here.';
    case 'all':
      return 'When members flag content, those reports will land in this queue.';
  }
}

// Subject deep-link resolver. The API doesn't surface slugs, so we have to
// fall back to the raw id for venue / artist (admin can paste it). Message
// + review are best linked from the subject_id directly.
function resolveSubjectHref(r: ReportItem): string | null {
  switch (r.subject_type) {
    case 'venue':
      // No slug available from the API; admin can use the venue id to look
      // up the listing manually. Returning null surfaces the id-only fallback.
      return null;
    case 'artist':
      return null;
    case 'message':
      // Messages don't have a stable public URL; the admin uses the id to
      // pivot via the inbox surfaces.
      return null;
    case 'review':
      return null;
    case 'exchange':
      // Exchanges are party-scoped; no public URL. The admin pivots from the
      // id via the exchange detail surfaces.
      return null;
  }
}

function formatRelative(epochMs: number): string {
  const now = Date.now();
  const diff = now - epochMs;
  if (diff < 60_000) return 'just now';
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  const years = Math.floor(days / 365);
  return `${years}y ago`;
}

function formatAbsolute(epochMs: number): string {
  return new Date(epochMs).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
